import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, AreaChart, Area, Legend } from 'recharts';
import { format, subDays } from 'date-fns';
import { motion } from 'framer-motion';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  RefreshCcw,
  Search,
  Globe,
  BarChart3,
} from 'lucide-react';
import { cn } from '../lib/utils';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; trend?: { value: number; unit: string }; icon: React.ReactNode; color: string }> =
  ({ label, value, trend, icon, color }) => (
    <motion.div
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
          {trend && (
            <div className={cn("text-[10px] font-bold mt-2", trend.value >= 0 ? "text-emerald-400" : "text-red-400")}>
              {trend.value >= 0 ? '+' : ''}{trend.value}{trend.unit} vs prev
            </div>
          )}
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

type TabType = 'yield-curve' | 'spreads' | 'rates';

interface SecurityData {
  cusip: string;
  description: string;
  price: number;
  yield: number;
  couponRate: number;
  maturityDate: string;
  securityType: string;
  source: string;
  // Real analytics from /api/bond-analytics (null when unavailable — never fabricated)
  modifiedDuration: number | null;
  convexity: number | null;
  oasSpread: number | null;        // basis points
  spreadToTreasury: number | null; // basis points
  dv01: number | null;
}

// ---- formatting helpers for the FRED-backed indicator cards ----
const fmtPct = (ind: any): string => (ind?.value != null ? `${Number(ind.value).toFixed(2)}%` : '—');
const fmtBps = (ind: any): string => (ind?.value != null ? `${Math.round(Number(ind.value))} bps` : '—');
const trendOf = (ind: any, unit: string): { value: number; unit: string } | undefined =>
  (ind?.change != null ? { value: Number(ind.change), unit } : undefined);
const fmtNum = (v: number | null, suffix = '', digits = 2): string =>
  (v != null ? `${v.toFixed(digits)}${suffix}` : 'N/A');

const QUICK_CUSIPS = [
  { cusip: '912828ZT6', label: '10Y Treasury' },
  { cusip: '912810SZ3', label: '30Y Treasury' },
  { cusip: '9128285L6', label: '5Y Treasury' },
  { cusip: '912810TM1', label: '20Y Treasury' },
];

const MarketDataScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('yield-curve');
  const [yieldData, setYieldData] = useState<any[]>([]);
  const [indicators, setIndicators] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);

  // Rates tab state
  const [ratesData, setRatesData] = useState<any[]>([]);
  const [currentRates, setCurrentRates] = useState<{ '2Y': number | null; '5Y': number | null; '10Y': number | null }>({ '2Y': null, '5Y': null, '10Y': null });
  const [ratesLoading, setRatesLoading] = useState(false);
  const [selectedRange, setSelectedRange] = useState(30);

  // Spreads tab state
  const [searchCusip, setSearchCusip] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [securityData, setSecurityData] = useState<SecurityData | null>(null);
  const [recentSearches, setRecentSearches] = useState<SecurityData[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);

  const fetchMarketData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [yieldRes, indicatorRes, currRes] = await Promise.all([
        axios.get('/api/finra-trace/yield-curve'),
        axios.get('/api/dashboard/market-indicators'),
        // Load current Treasury rates on mount so the 10Y header card is real on every tab,
        // not just after the Rates tab is opened.
        axios.get('/api/market-data/treasury-rates/treasury-gov/current').catch(() => ({ data: null }))
      ]);

      // Backend returns the yield curve points under `chartData` ([{tenor, yield}]).
      if (Array.isArray(yieldRes.data?.chartData)) {
        setYieldData(yieldRes.data.chartData);
      }
      setIndicators(indicatorRes.data || {});
      if (currRes.data) {
        setCurrentRates({
          '2Y': currRes.data['2Y'] ?? currRes.data['2y'] ?? null,
          '5Y': currRes.data['5Y'] ?? currRes.data['5y'] ?? null,
          '10Y': currRes.data['10Y'] ?? currRes.data['10y'] ?? null,
        });
      }
    } catch (error) {
      console.error('Error fetching market data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchRatesData = useCallback(async (days: number) => {
    setRatesLoading(true);
    try {
      const endDate = format(new Date(), 'yyyy-MM-dd');
      const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
      const [histRes, currRes] = await Promise.all([
        axios.get(`/api/market-data/treasury-rates/treasury-gov?startDate=${startDate}&endDate=${endDate}`),
        axios.get('/api/market-data/treasury-rates/treasury-gov/current')
      ]);
      if (histRes.data?.data) {
        setRatesData(histRes.data.data);
      }
      if (currRes.data) {
        setCurrentRates({
          '2Y': currRes.data['2Y'] ?? currRes.data['2y'] ?? null,
          '5Y': currRes.data['5Y'] ?? currRes.data['5y'] ?? null,
          '10Y': currRes.data['10Y'] ?? currRes.data['10y'] ?? null,
        });
      }
    } catch (error) {
      console.error('Error fetching rates:', error);
    } finally {
      setRatesLoading(false);
    }
  }, []);

  const searchSecurity = useCallback(async (cusip: string) => {
    if (!cusip.trim()) return;
    setSearchLoading(true);
    setSearchError(null);
    try {
      const upper = cusip.toUpperCase();
      const [secRes, priceRes, analyticsRes] = await Promise.all([
        axios.get(`/api/dimensions/securities/cusip/${upper}`).catch(() => null),
        axios.get(`/api/finra-trace/price/${upper}`),
        // Real calculated risk metrics (duration, convexity, OAS, DV01). Null when unavailable.
        axios.get(`/api/analytics/security/cusip/${upper}`).catch(() => null),
      ]);
      const sec = secRes?.data;
      const price = priceRes.data;
      const a = analyticsRes?.data?.analytics ?? {};
      const num = (v: any): number | null => (v === null || v === undefined || v === '' ? null : Number(v));
      const result: SecurityData = {
        cusip: upper,
        description: sec?.securityName || analyticsRes?.data?.securityName || price?.description || upper,
        price: price?.price ?? 100,
        yield: num(a.yieldToMaturity) ?? price?.yield ?? 0,
        couponRate: price?.couponRate ?? sec?.couponRate ?? 0,
        maturityDate: price?.maturityDate ?? sec?.maturityDate ?? '',
        securityType: sec?.assetClassName || (cusip.startsWith('912') ? 'Treasury' : 'Corporate'),
        source: price?.source || 'Estimated',
        modifiedDuration: num(a.modifiedDuration) ?? num(a.effectiveDuration),
        convexity: num(a.convexity),
        oasSpread: num(a.optionAdjustedSpread),
        spreadToTreasury: num(a.spreadToTreasury),
        dv01: num(a.dv01),
      };
      setSecurityData(result);
      setRecentSearches(prev => {
        const filtered = prev.filter(s => s.cusip !== result.cusip);
        return [result, ...filtered].slice(0, 5);
      });
    } catch (error: any) {
      setSearchError('Security not found or pricing unavailable');
      setSecurityData(null);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMarketData();
    const interval = setInterval(fetchMarketData, 300000);
    return () => clearInterval(interval);
  }, [fetchMarketData]);

  useEffect(() => {
    if (activeTab === 'rates') {
      fetchRatesData(selectedRange);
    }
  }, [activeTab, selectedRange, fetchRatesData]);

  const handleRangeChange = (days: number) => {
    setSelectedRange(days);
  };

  // ============== Tab Content Renderers ==============

  const renderYieldCurveTab = () => (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <GlassCard className="xl:col-span-2 p-6 h-[500px]">
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-400" />
            US Treasury Yield Curve
          </h3>
          <span className="px-2 py-1 bg-blue-500/10 text-blue-400 text-[10px] font-bold rounded uppercase">Real-Time</span>
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={yieldData}>
              <defs>
                <linearGradient id="colorYield" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="tenor" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
              <ChartTooltip
                contentStyle={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                itemStyle={{ color: '#fff', fontSize: '12px' }}
              />
              <Area type="monotone" dataKey="yield" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorYield)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-400" />
          Current Levels
        </h3>
        <div className="space-y-1">
          {yieldData.map((p: any) => (
            <div key={p.tenor} className="flex justify-between items-center p-2.5 rounded-lg hover:bg-white/5 transition-colors">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{p.tenor} Treasury</span>
              <span className="text-sm font-bold text-white">{p.yield != null ? `${Number(p.yield).toFixed(2)}%` : '—'}</span>
            </div>
          ))}
          <div className="h-px bg-white/5 my-2" />
          <div className="flex justify-between items-center p-2.5 rounded-lg hover:bg-white/5 transition-colors">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Fed Funds</span>
            <span className="text-sm font-bold text-white">{fmtPct(indicators.fedFundsRate)}</span>
          </div>
          <div className="flex justify-between items-center p-2.5 rounded-lg hover:bg-white/5 transition-colors">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">IG Spread</span>
            <span className="text-sm font-bold text-white">{fmtBps(indicators.igSpread)}</span>
          </div>
          <div className="flex justify-between items-center p-2.5 rounded-lg hover:bg-white/5 transition-colors">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">HY Spread</span>
            <span className="text-sm font-bold text-white">{fmtBps(indicators.hySpread)}</span>
          </div>
        </div>
        <div className="text-[10px] text-slate-600 mt-5 leading-relaxed">
          Treasury par yields via Treasury.gov · Fed Funds &amp; ICE BofA OAS via FRED
        </div>
      </GlassCard>
    </div>
  );

  const renderSpreadsTab = () => (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      {/* Search Panel */}
      <GlassCard className="p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Search className="w-5 h-5 text-blue-400" />
          Security Lookup
        </h3>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={searchCusip}
            onChange={(e) => setSearchCusip(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && searchSecurity(searchCusip)}
            placeholder="Enter CUSIP..."
            className="flex-1 px-4 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white text-sm font-mono placeholder:text-slate-600 focus:outline-none focus:border-blue-500"
          />
          <button
            onClick={() => searchSecurity(searchCusip)}
            disabled={searchLoading}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold rounded-xl transition-colors disabled:opacity-50"
          >
            {searchLoading ? '...' : 'Search'}
          </button>
        </div>
        <div className="mb-4">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Quick Access</div>
          <div className="flex flex-wrap gap-2">
            {QUICK_CUSIPS.map(q => (
              <button
                key={q.cusip}
                onClick={() => { setSearchCusip(q.cusip); searchSecurity(q.cusip); }}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-white/5 text-slate-300 text-[11px] font-mono rounded-lg transition-colors"
              >
                {q.cusip}
              </button>
            ))}
          </div>
        </div>
        {recentSearches.length > 0 && (
          <div>
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Recent Searches</div>
            <div className="space-y-1">
              {recentSearches.map(s => (
                <div
                  key={s.cusip}
                  onClick={() => { setSearchCusip(s.cusip); searchSecurity(s.cusip); }}
                  className="flex justify-between items-center p-2 rounded-lg hover:bg-white/5 cursor-pointer transition-colors"
                >
                  <div>
                    <div className="text-xs font-mono text-slate-300">{s.cusip}</div>
                    <div className="text-[10px] text-slate-500 truncate max-w-[180px]">{s.description}</div>
                  </div>
                  <div className="text-xs font-bold text-white">${s.price.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </GlassCard>

      {/* Security Detail */}
      <GlassCard className="xl:col-span-2 p-6">
        {searchError && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-red-400 text-sm font-bold mb-2">{searchError}</div>
              <div className="text-slate-500 text-xs">Try a different CUSIP or use Quick Access buttons</div>
            </div>
          </div>
        )}
        {!securityData && !searchError && (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <div className="text-center">
              <Search className="w-12 h-12 text-slate-700 mx-auto mb-4" />
              <div className="text-slate-400 text-sm font-bold mb-1">Search for a security</div>
              <div className="text-slate-600 text-xs">Enter a CUSIP to view pricing, analytics, and risk metrics</div>
            </div>
          </div>
        )}
        {securityData && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-start">
              <div>
                <div className="text-2xl font-mono font-bold text-white">{securityData.cusip}</div>
                <div className="text-sm text-slate-400 mt-1">{securityData.description}</div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-white">${securityData.price.toFixed(4)}</div>
                <span className={cn(
                  "text-[10px] font-bold px-2 py-0.5 rounded",
                  securityData.source.includes('Treasury') ? "bg-blue-500/20 text-blue-400" :
                  securityData.source === 'TRACE' ? "bg-emerald-500/20 text-emerald-400" :
                  "bg-amber-500/20 text-amber-400"
                )}>
                  {securityData.source}
                </span>
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Yield', value: securityData.yield ? `${securityData.yield.toFixed(3)}%` : 'N/A', color: '#3b82f6' },
                { label: 'Duration', value: fmtNum(securityData.modifiedDuration, 'y'), color: '#8b5cf6' },
                { label: 'OAS Spread', value: securityData.oasSpread != null ? `${securityData.oasSpread.toFixed(0)} bps` : 'N/A', color: '#10b981' },
                { label: 'Type', value: securityData.securityType, color: '#f59e0b' },
              ].map((m, i) => (
                <div key={i} className="p-4 bg-slate-900 rounded-xl border border-white/5">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">{m.label}</div>
                  <div className="text-lg font-bold text-white">{m.value}</div>
                </div>
              ))}
            </div>

            {/* Detail Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-900 rounded-xl border border-white/5">
                <div className="text-xs font-bold text-slate-400 mb-3">Security Details</div>
                <div className="space-y-2">
                  {[
                    { label: 'Coupon Rate', value: securityData.couponRate ? `${securityData.couponRate}%` : 'N/A' },
                    { label: 'Maturity Date', value: securityData.maturityDate || 'N/A' },
                    { label: 'Security Type', value: securityData.securityType },
                    { label: 'Price Source', value: securityData.source },
                  ].map((d, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-[11px] text-slate-500">{d.label}</span>
                      <span className="text-[11px] font-bold text-slate-300">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4 bg-slate-900 rounded-xl border border-white/5">
                <div className="text-xs font-bold text-slate-400 mb-3">Risk Analytics</div>
                <div className="space-y-2">
                  {[
                    { label: 'Modified Duration', value: fmtNum(securityData.modifiedDuration, '', 3) },
                    { label: 'Convexity', value: fmtNum(securityData.convexity, '', 3) },
                    { label: 'Spread to Treasury', value: securityData.spreadToTreasury != null ? `${securityData.spreadToTreasury.toFixed(0)} bps` : 'N/A' },
                    { label: 'DV01', value: fmtNum(securityData.dv01, '', 4) },
                  ].map((d, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-[11px] text-slate-500">{d.label}</span>
                      <span className="text-[11px] font-bold text-slate-300">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </GlassCard>
    </div>
  );

  const renderRatesTab = () => (
    <div className="space-y-6">
      {/* Current Rates Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: '2-Year Treasury', key: '2Y' as const, color: '#8b5cf6' },
          { label: '5-Year Treasury', key: '5Y' as const, color: '#a855f7' },
          { label: '10-Year Treasury', key: '10Y' as const, color: '#c084fc' },
        ].map(r => (
          <GlassCard key={r.key} className="p-6 border-l-4" style={{ borderLeftColor: r.color }}>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{r.label}</div>
            <div className="text-3xl font-bold text-white">
              {currentRates[r.key] !== null ? `${currentRates[r.key]!.toFixed(2)}%` : '—'}
            </div>
          </GlassCard>
        ))}
      </div>

      {/* Historical Chart */}
      <GlassCard className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-400" />
            Historical Treasury Rates
          </h3>
          <div className="flex gap-2">
            {[
              { label: '7D', days: 7 },
              { label: '30D', days: 30 },
              { label: '90D', days: 90 },
              { label: '1Y', days: 365 },
            ].map(p => (
              <button
                key={p.days}
                onClick={() => handleRangeChange(p.days)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-bold transition-all",
                  selectedRange === p.days ? "bg-purple-600 text-white shadow-lg" : "bg-slate-800 text-slate-400 hover:text-white border border-white/5"
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="h-[400px]">
          {ratesLoading ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCcw className="w-6 h-6 text-slate-500 animate-spin" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={ratesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis
                  dataKey="date"
                  stroke="#64748b"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => { try { return format(new Date(v), 'MMM dd'); } catch { return v; } }}
                />
                <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
                <ChartTooltip
                  contentStyle={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                  itemStyle={{ fontSize: '12px' }}
                  labelFormatter={(v) => { try { return format(new Date(v), 'MMM dd, yyyy'); } catch { return v; } }}
                />
                <Legend />
                <Line type="monotone" dataKey="2Y" stroke="#8b5cf6" strokeWidth={2} dot={false} name="2Y" />
                <Line type="monotone" dataKey="5Y" stroke="#a855f7" strokeWidth={2} dot={false} name="5Y" />
                <Line type="monotone" dataKey="10Y" stroke="#c084fc" strokeWidth={2} dot={false} name="10Y" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </GlassCard>
    </div>
  );

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <Globe className="w-8 h-8 text-blue-500" />
                Market Intel
              </h1>
              <p className="text-slate-500 font-medium mt-1">Institutional cross-asset market data and yield curve analytics</p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl">
                {(['yield-curve', 'spreads', 'rates'] as TabType[]).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={cn(
                      "px-4 py-1.5 rounded-lg text-xs font-bold transition-all capitalize",
                      activeTab === tab ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300"
                    )}
                  >
                    {tab === 'yield-curve' ? 'Yield Curve' : tab === 'spreads' ? 'Spreads' : 'Rates'}
                  </button>
                ))}
              </div>
              <button
                onClick={() => { fetchMarketData(); if (activeTab === 'rates') fetchRatesData(selectedRange); }}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn("w-4 h-4", isLoading && "animate-spin")} />
                Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="10Y Treasury" value={currentRates['10Y'] !== null ? `${currentRates['10Y']!.toFixed(2)}%` : '—'} icon={<TrendingDown className="w-5 h-5" />} color="#ef4444" />
            <StatCard label="Fed Funds" value={fmtPct(indicators.fedFundsRate)} trend={trendOf(indicators.fedFundsRate, '%')} icon={<Activity className="w-5 h-5" />} color="#3b82f6" />
            <StatCard label="IG Index Spread" value={fmtBps(indicators.igSpread)} trend={trendOf(indicators.igSpread, ' bps')} icon={<TrendingUp className="w-5 h-5" />} color="#10b981" />
            <StatCard label="HY Index Spread" value={fmtBps(indicators.hySpread)} trend={trendOf(indicators.hySpread, ' bps')} icon={<BarChart3 className="w-5 h-5" />} color="#8b5cf6" />
          </div>
        </motion.div>

        {activeTab === 'yield-curve' && renderYieldCurveTab()}
        {activeTab === 'spreads' && renderSpreadsTab()}
        {activeTab === 'rates' && renderRatesTab()}
      </div>
    </Container>
  );
};

export default MarketDataScreen;
